import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Trophy, Globe, Calendar, MapPin, Mail, Clock, ClipboardList, Music, FileText } from "lucide-react";
import { usePlanning } from "../lib/usePlanning";
import { courseSlug } from "../lib/planningHelpers";
import { useSEO } from "../lib/useSEO";
import { loadYouTubeApi } from "../lib/youtubeApi";
import { useIsTouchDevice } from "../lib/useIsTouchDevice";
import { thumb, thumbWide, fallbackToFull } from "../lib/imageThumb";
import { AUDITION, auditionPassee } from "../lib/auditions";
import { useReglages } from "../lib/useReglages";

// La date de l'audition vient de la console (Réglages → Saison) via lib/auditions.js :
// une seule date pour tout le site, et plus de date affichée une fois qu'elle est passée.
const REGLEMENT_COMPETITION_URL = "/docs/reglement-competition-compagnie.pdf";
const AUDITION_ADDRESS = "1000 Arts, Chemin des Dailles 10E, 1053 Cugy VD";
const AUDITION_EMAIL = "info1000arts@gmail.com";

const AUDITION_SLOTS = [
  { group: "Groupe Compagnie (comédie musicale avancée)", time: "12h30 – 15h30", room: "Salle du haut" },
  { group: "Groupes Compétition (hip-hop)", time: "14h – 17h", room: "Salle du bas" },
];

const TROUPES = [
  { name: "Rising Arts", cat: "Children Small Group (moins de 12 ans)", result: "7ᵉ place — finale des Championnats Suisses",
    poster: "/img/e6dc0890f8.webp",
    video: { provider: "youtube", id: "Hv9v75HfTRs" } },
  { name: "1000 Sparks", cat: "Children Small Group (moins de 12 ans)", result: "8ᵉ place — finale des Championnats Suisses",
    poster: "/img/c0923818a1.webp",
    video: { provider: "youtube", id: "ZCF15ZYL7nc" } },
  { name: "Kiara & Giulia", cat: "Duo Junior (12–17 ans)", result: "3ᵉ place — qualifiées pour les Championnats du Monde",
    poster: "/kg-duo.jpg",
    video: { provider: "youtube", id: "7QNUPaXFVuc" } },
  { name: "Claudio", cat: "Solo Homme Adulte", result: "4ᵉ place — qualifié pour les Championnats du Monde.",
    poster: "/img/8a637e9bf3.webp",
    video: { provider: "youtube", id: "jujG6OL4zHU" } },
];

const KPIS = [
  // Le nombre d'élèves est lu en direct dans la console (GET /api/reglages).
  { icon: Users,  value: null, label: "élèves actifs" },
  { icon: Trophy, value: "1000+", label: "spectateurs lors des spectacles" },
  { icon: Globe,  value: "2",    label: "qualifications aux Championnats du Monde" },
];

// Liste les créneaux de compétition du planning (Sheet) : Discipline = « Compétition ».
// Une carte PAR séance, sans regroupement : deux séances du même nom mais d'âges
// différents (ex. Compagnie junior 7-14 ans et 7-11 ans) doivent apparaître séparément,
// et le total doit correspondre exactement au filtre « Compétition » du planning.
const ORDRE_JOURS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

function listCompetitionCourses(courses) {
  return (courses || [])
    .filter((c) => c.discipline === "Compétition")
    .map((c) => ({
      key: `${c.day}-${c.start}-${c.name}`,
      name: c.name,
      slug: courseSlug(c),
      age_label: c.age_label,
      day: c.day,
      start: c.start,
      end: c.end,
      room: c.room,
    }))
    .sort((a, b) => {
      const d = ORDRE_JOURS.indexOf(a.day) - ORDRE_JOURS.indexOf(b.day);
      return d !== 0 ? d : String(a.start).localeCompare(String(b.start));
    });
}

export default function Competition() {
  const { eleves_actifs: nbEleves } = useReglages();
  const AUDITION_DATE = AUDITION.date_label;
  const auditionFermee = auditionPassee();
  useSEO({
    title: "Compétition Hip-Hop & Compagnie de comédie musicale — 1000 Arts Cugy VD",
    description: "1000 Arts prépare ses élèves aux compétitions de hip-hop et fait vivre une Compagnie de comédie musicale en Suisse romande. Auditions à Cugy VD, canton de Vaud. Finalistes des Championnats Suisses, deux qualifications aux Championnats du Monde.",
    canonical: "/competition",
  });

  const { courses } = usePlanning();
  const competitionCourses = useMemo(() => listCompetitionCourses(courses), [courses]);

  return (
    <div data-testid="page-competition" className="pt-32 pb-24">
      {/* ============= SECTION A — Hero + KPI ============= */}
      <section className="relative overflow-hidden">
        {/* Bulles bleues décoratives — même style que la Home */}
        <div className="absolute -top-16 -right-32 w-[560px] h-[560px] hero-shape pointer-events-none" style={{ background: "var(--primary-soft)", opacity: 0.55 }} aria-hidden="true" />
        <div className="absolute top-40 -left-24 w-[380px] h-[380px] hero-shape pointer-events-none" style={{ opacity: 0.35 }} aria-hidden="true" />
        <div className="absolute bottom-0 right-1/3 w-[240px] h-[240px] hero-shape pointer-events-none" style={{ opacity: 0.25 }} aria-hidden="true" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-6 items-center">
          {/* Colonne texte (6/12) */}
          <div className="md:col-span-6 relative z-10">
            <div className="eyebrow mb-3">Filière compétition &amp; compagnie</div>
            <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-[1.05]">
              Une école qui monte{" "}<br /><span style={{ color: "var(--primary)" }}>sur les podiums</span>
            </h1>
            <p className="mt-6 text-lg text-[var(--muted)] max-w-xl leading-relaxed">
              1000 Arts n'est pas une simple école de loisirs. Elle prépare ses élèves aux compétitions de hip-hop et fait vivre une Compagnie de comédie musicale qui tourne en Suisse romande. Dès sa première année de compétition, l'école a placé ses groupes en finale des Championnats Suisses et qualifié deux de ses danseurs pour les Championnats du Monde.
            </p>
          </div>

          {/* Colonne photo (6/12) — même cadrage que la Home (rounded + shadow + gradient turquoise) */}
          <div className="md:col-span-6 relative z-10" data-testid="competition-hero-photo">
            <div
              className="relative rounded-[32px] overflow-hidden shadow-2xl aspect-[4/5] lg:aspect-[5/6]"
              style={{ background: "linear-gradient(135deg, #66EAF0 0%, #00C6CE 100%)" }}
            >
              <img
                src={thumbWide("/img/e5b68325e6.webp")} onError={fallbackToFull("/img/e5b68325e6.webp")}
                alt="Élèves du groupe compétition 1000 ARTS en pleine chorégraphie hip-hop"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>

        {/* KPI bandeau */}
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 mt-14 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="competition-kpis">
          {KPIS.map((k, i) => {
            const Icon = k.icon;
            return (
              <div
                key={i}
                data-testid={`kpi-${i}`}
                className="rounded-2xl p-6 md:p-8 flex items-center gap-5"
                style={{ background: "var(--primary-tint)" }}
              >
                <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--primary)", color: "#fff" }}>
                  <Icon size={26} />
                </div>
                <div>
                  <div className="font-display text-4xl md:text-5xl leading-none" style={{ color: "var(--primary)" }}
                    data-testid={k.value === null ? "competition-nb-eleves" : undefined}>
                    {k.value === null ? (nbEleves || 230) : k.value}
                  </div>
                  <div className="text-sm text-[var(--muted)] mt-1">{k.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ============= SECTION B — Comment nous rejoindre ============= */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mt-24" data-testid="competition-audition">
        {/* Intro 2 colonnes : texte à gauche + photo à droite */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
          <div className="md:col-span-6">
            <div className="eyebrow mb-3">Auditions</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight">Comment nous rejoindre&nbsp;?</h2>
            <p className="mt-6 text-lg text-[var(--text)]">
              L'accès aux cours de compétition et à la Compagnie passe <strong>obligatoirement par l'audition</strong>.
            </p>
            <p className="mt-3 text-lg text-[var(--muted)]">
              Tu as entre 7 et 16 ans, tu rêves de participer à des compétitions de hip-hop en solo, en duo ou en groupe, ou à des spectacles mêlant chant, théâtre, danses urbaines et contemporaines&nbsp;? Alors tente ta chance&nbsp;!
            </p>
          </div>

          {/* Photo "Rejoins-nous" — cadrage cohérent avec la Home / Hero (rounded + shadow) */}
          <div className="md:col-span-6" data-testid="competition-join-photo">
            <div
              className="relative rounded-[28px] overflow-hidden shadow-2xl aspect-[4/3]"
              style={{ background: "linear-gradient(135deg, #66EAF0 0%, #00C6CE 100%)" }}
            >
              <img
                src={thumbWide("/img/85d2ee30be.webp")} onError={fallbackToFull("/img/85d2ee30be.webp")}
                alt="Groupe 1000 ARTS sur scène en tenue rose corail — rejoins-nous"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        {/* Bloc audition — glassmorphique en dégradé turquoise (couleur primaire du site) */}
        <div
          className="mt-12 rounded-[28px] overflow-hidden shadow-2xl relative max-w-4xl"
          style={{
            background: "linear-gradient(135deg, var(--primary) 0%, #00A8B0 60%, #008A91 100%)",
            color: "#fff",
          }}
          data-testid="audition-details"
        >
          {/* Décor : vaguelettes/texture subtile en haut à droite */}
          <div className="absolute inset-0 pointer-events-none opacity-30" aria-hidden="true"
            style={{
              background: "radial-gradient(circle at top right, rgba(255,255,255,0.25) 0%, transparent 50%)",
            }}
          />

          <div className="relative p-6 md:p-10">
            {/* Pill "Audition annuelle" */}
            <div
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-5"
              style={{ background: "rgba(255,255,255,0.14)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.22)" }}
            >
              <Trophy size={14} /> Audition annuelle
            </div>

            {/* Audition passée et date suivante pas encore posée dans la console : pas
                d'horaire, de public ni de consigne d'une audition qui n'existe pas encore
                (retour de l'école, 09.10.2026). Dès que l'école pose la date suivante dans
                Réglages → Saison, l'encart complet revient tout seul. */}
            {auditionFermee ? (
              <div data-testid="competition-audition-passee">
                <h3 className="font-display text-3xl md:text-5xl leading-none tracking-tight mb-4" data-testid="competition-audition-date">
                  L'audition du {AUDITION_DATE.charAt(0).toLowerCase() + AUDITION_DATE.slice(1)} est passée
                </h3>
                <p className="text-white/90 text-base md:text-lg max-w-2xl leading-relaxed">
                  Merci à toutes et à tous ! Les informations pour les <strong className="text-white">prochaines auditions</strong> sont
                  à venir : la date sera annoncée ici dès qu'elle sera fixée.
                </p>
                <div className="mt-8 border-t border-white/25 pt-6 flex items-start gap-2 text-white/90 text-sm">
                  <Mail size={16} className="mt-0.5 shrink-0 opacity-80" />
                  <span>
                    Une question sur la Compétition ou la Compagnie ? Écris-nous à{" "}
                    <a href={`mailto:${AUDITION_EMAIL}`} className="underline font-semibold text-white">{AUDITION_EMAIL}</a>.
                  </span>
                </div>
              </div>
            ) : (
            <>
            {/* Grande date */}
            <h3 className="font-display text-3xl md:text-5xl leading-none tracking-tight mb-2" data-testid="competition-audition-date">
              {AUDITION_DATE}
            </h3>

            {/* Adresse */}
            <p className="flex items-center gap-2 text-white/90 text-sm md:text-base">
              <MapPin size={16} className="opacity-80" /> {AUDITION_ADDRESS}
            </p>

            {/* 3 sous-cartes glassmorphiques */}
            <div className="mt-6 grid md:grid-cols-3 gap-3">
              <GlassStat icon={Users} label="Groupe Compagnie" value="12h30 – 15h30" note="Salle du haut · comédie musicale" />
              <GlassStat icon={Trophy} label="Groupes Compétition" value="14h – 17h" note="Salle du bas · hip-hop" />
              <GlassStat icon={Users} label="Public" value="7 – 16 ans" note="Débutants bienvenus" />
            </div>

            {/* Séparateur horizontal */}
            <div className="mt-8 border-t border-white/25" />

            {/* Instructions en 3 rows avec petits icônes en rond */}
            <div className="mt-6 space-y-3">
              <InfoRow icon={ClipboardList}>
                <strong className="text-white">Inscription obligatoire</strong> via le formulaire dédié.
              </InfoRow>
              <InfoRow icon={Clock}>
                Merci d'arriver <strong className="text-white">15 minutes en avance</strong>.
              </InfoRow>
              <InfoRow icon={Music}>
                <strong className="text-white">À préparer</strong> — audition Compagnie uniquement : une chanson d'environ 1 min 30, dans le registre Disney ou comédie musicale.
              </InfoRow>
            </div>

            {/* CTA + note */}
            <div className="mt-8 border-t border-white/25 pt-6 flex flex-wrap items-center gap-5">
              <Link
                to="/inscription/audition"
                data-testid="cta-audition"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-display text-lg md:text-xl shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all"
                style={{ background: "#fff", color: "var(--primary)" }}
              >
                S'inscrire à l'audition <ArrowRight size={18} />
              </Link>
              <div className="flex items-start gap-2 text-white/85 text-xs md:text-sm italic max-w-md">
                <Calendar size={14} className="mt-0.5 shrink-0 opacity-80" />
                Date choisie pour éviter le week-end du Jeûne fédéral.
              </div>
            </div>
            </>
            )}
          </div>
        </div>

        {/* Horaires hebdomadaires (data-driven) */}
        <div className="mt-16" data-testid="competition-weekly">
          <h3 className="font-display text-2xl md:text-3xl tracking-tight">Horaires hebdomadaires — Compétition &amp; Compagnie</h3>
          <p className="mt-2 text-sm text-[var(--muted)]">Distinct de l'audition ci-dessus. Ces créneaux hebdomadaires sont réservés aux danseurs sélectionnés à l'issue de l'audition.</p>

          {competitionCourses.length === 0 ? (
            <div className="mt-6 card-flat text-center text-[var(--muted)]" data-testid="competition-weekly-fallback">
              Horaires temporairement indisponibles — contactez-nous à <a href={`mailto:${AUDITION_EMAIL}`} className="underline">{AUDITION_EMAIL}</a>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {competitionCourses.map((c) => (
                <div
                  key={c.key}
                  data-testid={`comp-course-${c.slug || c.name}-${c.day}`}
                  className="rounded-2xl border border-[var(--border-soft)] bg-white p-5 hover:shadow-md transition-all"
                  style={{ borderLeft: "4px solid var(--primary)" }}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    {c.slug ? (
                      <Link
                        to={`/cours/${c.slug}`}
                        className="font-display text-lg leading-tight hover:underline"
                        style={{ textDecorationColor: "var(--primary)" }}
                      >
                        {c.name}
                      </Link>
                    ) : (
                      <span className="font-display text-lg leading-tight">{c.name}</span>
                    )}
                    <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold shrink-0" style={{ background: "var(--primary-tint)", color: "var(--primary)" }}>
                      {c.age_label}
                    </span>
                  </div>
                  <ul className="mt-2 space-y-1 text-sm text-[var(--muted)]">
                    <li className="flex items-center gap-2">
                      <Clock size={12} style={{ color: "var(--primary)" }} />
                      <strong className="text-[var(--text)] font-medium">{c.day}</strong> {c.start} – {c.end} · {c.room}
                    </li>
                  </ul>
                </div>
              ))}
            </div>
          )}

          <Link to="/planning" className="inline-flex items-center gap-1 mt-6 text-sm font-medium" style={{ color: "var(--primary)" }}>
            Voir le planning complet <ArrowRight size={14} />
          </Link>
        </div>

        {/* Bloc explicatif Compagnie */}
        <div className="mt-16 rounded-3xl p-6 md:p-10" style={{ background: "var(--primary-tint)", borderLeft: "4px solid var(--primary)" }} data-testid="compagnie-details">
          <div className="eyebrow mb-3" style={{ color: "var(--primary)" }}>Compagnie · Formation poussée</div>
          <h3 className="font-display text-2xl md:text-3xl tracking-tight">Les trois arts de la comédie musicale</h3>
          <p className="mt-5 text-[var(--text)] leading-relaxed">
            C'est une formation poussée dans les trois arts de la comédie musicale. Des professeurs spécialisés dans la <strong>danse urbaine</strong>, la <strong>danse contemporaine</strong>, le <strong>chant</strong> et le <strong>théâtre</strong> donneront les cours.
          </p>
          <p className="mt-3 text-[var(--text)] leading-relaxed">
            Les élèves doivent au minimum suivre <strong>3 cours de chant privé par année</strong> (obligatoires, non inclus dans le prix, avec 20 % de réduction sur ces cours) et montrer un travail assidu tout au long de l'année.
          </p>
          <p className="mt-3 text-[var(--text)] leading-relaxed">
            À la clé : <strong>une tournée de spectacles</strong> en compagnie d'adultes semi-pros et professionnels dans le Gros-de-Vaud.
          </p>
        </div>
      </section>

      {/* ============= SECTION C — Découvre nos troupes ============= */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mt-24" data-testid="competition-troupes">
        <div className="eyebrow mb-3">Nos troupes en compétition</div>
        <h2 className="font-display text-4xl md:text-5xl tracking-tight max-w-3xl">Découvre nos troupes</h2>
        <p className="mt-6 text-lg text-[var(--muted)] max-w-3xl">
          Pour leur toute première année en compétition, nos groupes ont atteint la finale des Championnats Suisses. Deux de nos danseurs sont qualifiés pour les Championnats du Monde et représenteront 1000 Arts — et la Suisse.
        </p>

        {/* Bandeau bleu — highlight des palmarès (contraste + relief) */}
        <div
          className="mt-8 rounded-2xl px-6 py-5 flex items-start gap-4"
          style={{ background: "var(--primary-tint)", borderLeft: "4px solid var(--primary)" }}
          data-testid="competition-highlight"
        >
          <Trophy size={22} style={{ color: "var(--primary)" }} className="mt-0.5 shrink-0" />
          <div className="text-sm md:text-base leading-relaxed" style={{ color: "var(--text)" }}>
            <strong style={{ color: "var(--primary)" }}>Palmarès inaugural</strong> — 4 groupes finalistes des Championnats Suisses IDO, dont 2 qualifications pour les Championnats du Monde. Rejoins l'aventure dès la prochaine saison.
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {TROUPES.map((t, i) => (
            <TroupeCard key={i} troupe={t} idx={i} />
          ))}
        </div>
      </section>

      {/* ============= Bloc final "Plus qu'une école, une famille" ============= */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 mt-24">
        <div
          className="rounded-3xl p-10 md:p-16 text-center"
          style={{ background: "var(--primary)", color: "#fff" }}
          data-testid="competition-final-cta"
        >
          <div className="eyebrow mb-3" style={{ color: "rgba(255,255,255,0.85)" }}>Rejoins-nous</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight max-w-3xl mx-auto">Plus qu'une école, une famille</h2>
          <p className="mt-6 text-lg text-white/90 max-w-2xl mx-auto">
            Prêt·e à tenter l'aventure&nbsp;? {auditionFermee
              ? "Écris-nous pour connaître la date de la prochaine audition et vivre ta première expérience compétitive avec 1000 Arts."
              : `Rejoins nos auditions le ${AUDITION_DATE.toLowerCase()} et vis ta première expérience compétitive avec 1000 Arts.`}
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link
              to="/inscription/audition"
              data-testid="cta-audition-final"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-[var(--primary)] font-semibold"
            >
              S'inscrire à l'audition <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-white text-white font-semibold hover:bg-white hover:text-[var(--primary)] transition-colors"
            >
              Nous contacter
            </Link>
          </div>
          <p className="mt-8 text-sm text-white/80 max-w-2xl mx-auto leading-relaxed">
            Les groupes Compétition et Compagnie sont encadrés par un règlement spécifique
            (assiduité, ponctualité, esprit d'équipe, frais de compétition et de costumes). Il doit
            être signé par l'élève et son représentant légal avant la première participation.
          </p>
          <a
            href={REGLEMENT_COMPETITION_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="competition-reglement-link"
            className="inline-flex items-center gap-2 mt-4 px-6 py-3 rounded-full border-2 border-white/60 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
          >
            <FileText size={15} /> Télécharger le règlement Compétition &amp; Compagnie
          </a>
          <p className="mt-4 text-sm">
            <Link
              to="/reglement-competition"
              data-testid="competition-reglement-html-link"
              className="text-white/90 underline hover:text-white"
            >
              Lire le règlement Compétition &amp; Compagnie en ligne
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
}

/** Petite carte glassmorphique utilisée dans le bloc audition. */
function GlassStat({ icon: Icon, label, value, note }) {
  return (
    <div
      className="rounded-2xl p-5 flex items-center gap-4"
      style={{
        background: "rgba(255,255,255,0.14)",
        backdropFilter: "blur(12px)",
        border: "1px solid rgba(255,255,255,0.22)",
      }}
    >
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
        style={{ background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.25)" }}
      >
        <Icon size={22} color="#fff" strokeWidth={1.8} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-widest opacity-80 mb-1">{label}</div>
        <div className="font-display text-xl leading-tight">{value}</div>
        <div className="text-xs mt-0.5 text-white/80">{note}</div>
      </div>
    </div>
  );
}

/** Ligne d'info avec petit rond icônique blanc. */
function InfoRow({ icon: Icon, children }) {
  return (
    <div className="flex items-start gap-4 text-white/95">
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
        style={{ background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.22)" }}
      >
        <Icon size={18} color="#fff" strokeWidth={1.8} />
      </div>
      <div className="text-sm md:text-base leading-relaxed pt-2">{children}</div>
    </div>
  );
}

/**
 * Carte troupe compétition.
 * - Desktop : la photo laisse place à la vidéo au survol, avec le son (le survol vaut
 *   geste utilisateur, le navigateur autorise donc le son).
 * - Mobile / tablette : pas de survol possible, donc la vidéo démarre d'elle-même EN MUET
 *   (seule lecture automatique autorisée), pilotée par l'API YouTube, avec le bouton son
 *   toujours disponible. Aucune tentative d'activer le son automatiquement : le navigateur
 *   la refuserait, ce qui ferait décrocher la lecture.
 */
function TroupeCard({ troupe, idx }) {
  const [hover, setHover] = React.useState(false);
  const [muted, setMuted] = React.useState(true);
  const iframeRef = React.useRef(null);
  const playerRef = React.useRef(null);
  const isTouch = useIsTouchDevice();
  const [ytPlaying, setYtPlaying] = React.useState(false);
  const hasVideo = !!troupe.video;
  const playing = hasVideo && (hover || isTouch);
  // La photo ne s'effimace que lorsque la vidéo joue vraiment : si la lecture est
  // refusée par le navigateur, on garde une belle image plutôt qu'un carré noir.
  const posterHidden = playing && (isTouch ? ytPlaying : true);

  const postCommand = (func) => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) return;
    iframe.contentWindow.postMessage(JSON.stringify({ event: "command", func, args: "" }), "*");
  };

  const applySound = (wantSoundNow) => {
    const p = playerRef.current;
    if (p) {
      try {
        if (wantSoundNow) { p.unMute(); p.setVolume(100); } else { p.mute(); }
      } catch (e) {}
    } else {
      postCommand(wantSoundNow ? "unMute" : "mute");
    }
    setMuted(!wantSoundNow);
  };

  // Sur mobile : on force la lecture via l'API officielle YouTube et on reste en muet.
  React.useEffect(() => {
    if (!playing || !isTouch) return undefined;
    let cancelled = false;
    let retry;
    loadYouTubeApi().then(() => {
      if (cancelled || !iframeRef.current || !window.YT?.Player) return;
      playerRef.current = new window.YT.Player(iframeRef.current, {
        events: {
          onReady: () => {
            if (cancelled) return;
            try { playerRef.current.playVideo(); } catch (e) {}
            retry = setTimeout(() => {
              try {
                if (playerRef.current?.getPlayerState?.() !== 1) playerRef.current.playVideo();
              } catch (e) {}
            }, 1200);
          },
          onStateChange: (e) => {
            if (!cancelled && e.data === 1) setYtPlaying(true);
          },
        },
      });
    });
    return () => {
      cancelled = true;
      clearTimeout(retry);
      setYtPlaying(false);
      playerRef.current = null;
    };
  }, [playing, isTouch]);

  // Desktop uniquement : au survol on active le son une fois l'iframe prête.
  React.useEffect(() => {
    if (isTouch) return undefined;
    if (hover) {
      const t = setTimeout(() => applySound(true), 500);
      return () => clearTimeout(t);
    }
    setMuted(true);
    return undefined;
  }, [hover, isTouch]);

  return (
    <article
      data-testid={`troupe-${idx}`}
      className="rounded-3xl bg-white overflow-hidden flex flex-col hover:shadow-xl transition-all"
      style={{ borderTop: "5px solid var(--primary)", boxShadow: "0 4px 20px rgba(15,27,45,0.06)" }}
      onMouseEnter={() => hasVideo && setHover(true)}
      onMouseLeave={() => hasVideo && setHover(false)}
    >
      <div
        className="relative w-full overflow-hidden"
        style={{ aspectRatio: "9 / 16", maxHeight: 480 }}
      >
        {/* Poster photo (visible avant hover) */}
        {troupe.poster && (
          <img loading="lazy" decoding="async"
            src={thumb(troupe.poster)} onError={fallbackToFull(troupe.poster)}
            alt={`${troupe.name} — 1000 ARTS`}
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            style={{ opacity: posterHidden ? 0 : 1, transition: "opacity 0.25s", zIndex: 10 }}
          />
        )}
        {/* Iframe YouTube — au survol sur desktop, en continu sur mobile */}
        {playing && (
          <iframe
            ref={iframeRef}
            data-testid={`troupe-${idx}-video`}
            title={`${troupe.name} — 1000 ARTS`}
            src={`https://www.youtube-nocookie.com/embed/${troupe.video.id}?autoplay=1&mute=1&loop=1&playlist=${troupe.video.id}&controls=0&modestbranding=1&rel=0&playsinline=1&iv_load_policy=3&enablejsapi=1`}
            className="absolute"
            style={{
              top: "50%",
              left: "50%",
              width: "max(100%, calc(100% * 16/9 * 9/16))",
              height: "max(100%, calc(100% * 16/9))",
              minWidth: "100%",
              minHeight: "100%",
              transform: "translate(-50%, -50%)",
              border: 0,
            }}
            allow="autoplay; encrypted-media; picture-in-picture"
          />
        )}
        {/* Bouton son — uniquement quand la vidéo est réellement visible */}
        {playing && posterHidden && (
          <button
            type="button"
            onClick={() => applySound(muted)}
            data-testid={`troupe-${idx}-mute`}
            aria-label={muted ? "Activer le son" : "Couper le son"}
            className="absolute top-4 right-4 z-20 px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg backdrop-blur"
            style={{ background: "rgba(255,255,255,0.92)", color: "var(--primary)" }}
          >
            {muted ? "🔊 Son" : "🔇 Muet"}
          </button>
        )}
      </div>
      <div className="p-6">
        <div className="eyebrow text-[10px]" style={{ color: "var(--primary)" }}>{troupe.cat}</div>
        <h3 className="font-display text-2xl mt-2">{troupe.name}</h3>
        <p className="mt-3 text-sm text-[var(--text)] leading-relaxed">{troupe.result}</p>
      </div>
    </article>
  );
}
